import { RouteObject, createBrowserRouter } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { atlasRoutes } from "@/app/router/routes";
import { AtlasRootOutlet } from "@/app/outlets/root";
import { RootPage } from "@/pages/root/page";
import { LogInPage } from "@/pages/log-in/page";
import { LogInEmailPasswordPage } from "@/pages/log-in-email-password/page";
import { LogOutPage } from "@/pages/log-out/page";
import { NotFoundPage } from "@/pages/not-found/page";
import { GoogleOAuthRedirectPage } from "@/pages/google-oauth-redirect/page";
import { AtlasStoreProviderOutlet } from "@/app/outlets/store-provider";
import { RequireAtlasAuthOutlet } from "@/app/outlets/require-auth";
import { HomePage } from "@/pages/home/page";
import { NotificationsPage } from "@/pages/notifications/page";
import { SettingsPage } from "@/pages/settings/page";
import { CollectionsViewPage } from "@/pages/collections/view/page";
import { SpaceAccountsRootPage } from "@/pages/space-accounts/root/page";
import { NotesViewPage } from "@/pages/notes/view/page";
import { NotesCreatePage } from "@/pages/notes/create/page";
import { NotesUploadAudioPage } from "@/pages/notes/upload-audio/page";
import { SpaceAccountsViewPage } from "@/pages/space-accounts/view/page";
import { CollectionsRootPage } from "@/pages/collections/root/page";
import { NotesRootPage } from "@/pages/notes/root/page";
import { AccountsRootPage } from "@/pages/accounts/root/page";
import { AccountsViewPage } from "@/pages/accounts/view/page";
import { AccountImportFromMem1Dot0Page } from "@/pages/accounts/import-from-mem-1-dot-0/page";
import { CompanyLinksPage } from "@/pages/company/links/page";
import { CompanyTeamPage } from "@/pages/company/team/page";
import { CompanyTeamMemberPage } from "@/pages/company/team/profile/page";
import { CompanyProfilePage } from "@/pages/company/profile/page";
import { NotesSnapshotsPage } from "@/pages/notes/snapshots/page";
import { SyncClientGroupsViewPage } from "@/pages/sync-client-groups/view/page";
import { SyncClientGroupsRootPage } from "@/pages/sync-client-groups/root/page";
import { SyncOperationsRootPage } from "@/pages/sync-operations/root/page";
import { SyncOperationsViewPage } from "@/pages/sync-operations/view/page";
import { SyncScopesViewPage } from "@/pages/sync-scopes/view/page";
import { SyncScopesRootPage } from "@/pages/sync-scopes/root/page";
import { SyncModelEventsViewPage } from "@/pages/sync-model-events/view/page";
import { SyncModelEventsRootPage } from "@/pages/sync-model-events/root/page";
import { SyncCvrsViewPage } from "@/pages/sync-cvrs/view/page";
import { SyncCvrsRootPage } from "@/pages/sync-cvrs/root/page";
import { SyncModelCvrSnapshotsRootPage } from "@/pages/sync-model-cvr-snapshots/root/page";
import { SyncModelCvrSnapshotsViewPage } from "@/pages/sync-model-cvr-snapshots/view/page";
import { SpaceAccountSyncClientGroupsPage } from "@/pages/space-accounts/sync-client-groups/page";
import { DataExportsCreatePage } from "@/pages/data-exports/create/page";
import { DataExportsRootPage } from "@/pages/data-exports/root/page";
import { DataExportsViewPage } from "@/pages/data-exports/view/page";
import { DataImportsRootPage } from "@/pages/data-imports/root/page";
import { DataImportsViewPage } from "@/pages/data-imports/view/page";
import { DataImportItemsViewPage } from "@/pages/data-import-items/view/page";
import { DataImportItemsRootPage } from "@/pages/data-import-items/root/page";
import { SpaceAccountDataImportsPage } from "@/pages/space-accounts/data-imports/page";
import { DataImportDataImportItemsPage } from "@/pages/data-imports/data-import-items/page";
import { AccountTransitionInfoRootPage } from "@/pages/account-transition-info/root/page";
import { AccountTransitionInfoViewPage } from "@/pages/account-transition-info/view/page";
import { AppCalloutsRootPage } from "@/pages/app-callouts/root/page";
import { AppCalloutsViewPage } from "@/pages/app-callouts/view/page";
import { SpaceAccountAppCalloutsViewPage } from "@/pages/space-account-app-callouts/view/page";
import { SpaceAccountAppCalloutsRootPage } from "@/pages/space-account-app-callouts/root/page";
import { SpaceAccountSpaceAccountAppCalloutsPage } from "@/pages/space-accounts/space-account-app-callouts/page";
import { BatchAccountUpgradeRootPage } from "@/pages/v1-batch-account-upgrade/root/page";
import { BatchAccountUpgradeViewPage } from "@/pages/v1-batch-account-upgrade/view/page";
import { BatchAccountUpgradeCreatePage } from "@/pages/v1-batch-account-upgrade/create/page";
import { AdminBadgesRootPage } from "@/pages/admin/badges/root/page";
import { AdminBadgesCreatePage } from "@/pages/admin/badges/create/page";
import { AdminBadgesViewPage } from "@/pages/admin/badges/view/page";
import { AdminMemberBadgesRootPage } from "@/pages/admin/member-badges/root/page";
import { AdminMemberBadgesCreatePage } from "@/pages/admin/member-badges/create/page";
import { AdminMemberBadgesViewPage } from "@/pages/admin/member-badges/view/page";
import { AdminLinearDashboardRootPage } from "@/pages/admin/linear-dashboard/root/page";
import { LegacyFirestoreUsersRootPage } from "@/pages/legacy-firestore-users/root/page";
import { LegacyFirestoreUsersViewPage } from "@/pages/legacy-firestore-users/view/page";
import { SessionsRootPage } from "@/pages/sessions/root/page";
import { SessionsCreatePage } from "@/pages/sessions/create/page";
import { SessionsViewPage } from "@/pages/sessions/view/page";
import { ElectricSessionsPage } from "@/pages/electric/sessions/page";
import { ElectricSessionEventsPage } from "@/pages/electric/session-events/page";
import { MembotSessionsListPage } from "@/pages/membot/sessions-list";
import { MembotSessionDetailPage } from "@/pages/membot/session-detail";
import { MembotThreadDetailPage } from "@/pages/membot/thread-detail";
import { MembotRunDetailPage } from "@/pages/membot/run-detail";
import { MembotSignalsPage } from "@/pages/membot/signals";
import { SessionEventsPageWrapper } from "@/pages/sessions/session-events/page-wrapper";
import { SessionArtifactViewPage } from "@/pages/sessions/artifacts/view/page";
import { SessionOperationViewPage } from "@/pages/sessions/operations/view/page";
import { ImportsRootPage } from "@/pages/imports/root/page";
import { ImportsDashboardPage } from "@/pages/imports/dashboard/page";
import { ImportsViewPage } from "@/pages/imports/view/page";
import { UploadedFilesRootPage } from "@/pages/utilities/uploaded-files/root/page";
import { UploadedFilesCreatePage } from "@/pages/utilities/uploaded-files/create/page";
import { UploadedFilesViewPage } from "@/pages/utilities/uploaded-files/view/page";
import { CalendarsRootPage } from "@/pages/utilities/calendars/root/page";
import { CapturedWebsitesRootPage } from "@/pages/utilities/captured-websites/root/page";
import { CapturedWebsitesCreatePage } from "@/pages/utilities/captured-websites/create/page";
import { CapturedWebsitesViewPage } from "@/pages/utilities/captured-websites/view/page";
import { SharedItemInvitesRootPage } from "@/pages/utilities/shared-item-invites/root/page";
import { SharedItemInvitesCreatePage } from "@/pages/utilities/shared-item-invites/create/page";
import { SharedItemInvitesViewPage } from "@/pages/utilities/shared-item-invites/view/page";
import { WebClipsRootPage } from "@/pages/utilities/web-clips/root/page";
import { WebClipsCreatePage } from "@/pages/utilities/web-clips/create/page";
import { WebClipsViewPage } from "@/pages/utilities/web-clips/view/page";
import { AppEventsRootPage } from "@/pages/utilities/app-events/root/page";
import { AppEventsViewPage } from "@/pages/utilities/app-events/view/page";
import { SmartViewsRootPage } from "@/pages/utilities/smart-views/root/page";
import { SmartViewsGetOrGeneratePage } from "@/pages/utilities/smart-views/get-or-generate/page";
import { SmartViewsViewPage } from "@/pages/utilities/smart-views/view/page";
import { SmartEngineRunPage } from "@/pages/utilities/smart-views/runs/page";
import { MonetizationPage } from "@/pages/monetization/page";
import { ApiRequestRecordsRootPage } from "@/pages/api-request-records/root/page";
import { ApiRequestRecordsViewPage } from "@/pages/api-request-records/view/page";
import { CapturedAudioRootPage } from "@/pages/captured-audio/root/page";
import { CapturedAudioViewPage } from "@/pages/captured-audio/view/page";
import { CapturedEmailsRootPage } from "@/pages/captured-emails/root/page";
import { CapturedEmailsViewPage } from "@/pages/captured-emails/view/page";
import { AccountMigrateEmailAddressPage } from "@/pages/accounts/migrate-email-address/page";
import { BillingRootPage } from "@/pages/billing/root/page";
import { BillingViewPage } from "@/pages/billing/view/page";
import { AudioRecordingsRootPage } from "@/pages/audio-recordings/root/page";
import { AudioRecordingsCreatePage } from "@/pages/audio-recordings/create/page";
import { AudioRecordingsQualityMetricsPage } from "@/pages/audio-recordings/quality-metrics/page";
import { AudioRecordingsViewPage } from "@/pages/audio-recordings/view/page";
import { NoteSuggestionsRootPage } from "@/pages/note-suggestions/root/page";
import { NoteSuggestionsViewPage } from "@/pages/note-suggestions/view/page";

export const AUTHENTICATED_ATLAS_ROUTES: RouteObject[] = [
  {
    path: atlasRoutes.root({}).path,
    element: <RequireAtlasAuthOutlet />,
    children: [
      /**
       * =============================================
       *               Home
       * =============================================
       */
      {
        path: atlasRoutes.home({}).path,
        element: <HomePage />,
      },
      {
        path: atlasRoutes.settings({}).path,
        element: <SettingsPage />,
      },
      {
        path: atlasRoutes.notifications({}).path,
        element: <NotificationsPage />,
      },

      /**
       * =============================================
       *               Company Routes
       * =============================================
       */
      {
        path: atlasRoutes.companyTeam({}).path,
        element: <CompanyTeamPage />,
      },
      {
        path: atlasRoutes.companyTeamMember({
          params: { staffMemberId: ":staffMemberId" },
        }).path,
        element: <CompanyTeamMemberPage />,
      },
      {
        path: atlasRoutes.companyLinks({}).path,
        element: <CompanyLinksPage />,
      },
      {
        path: atlasRoutes.companyProfile({}).path,
        element: <CompanyProfilePage />,
      },
      {
        path: atlasRoutes.company({}).path,
        element: <Navigate to={atlasRoutes.companyTeam({}).path} />,
      },

      /**
       * =============================================
       *            Account Routes
       * =============================================
       */
      {
        path: atlasRoutes.accounts({}).path,
        element: <AccountsRootPage />,
      },
      {
        path: atlasRoutes.accountsView({
          params: { accountId: ":accountId" },
        }).path,
        element: <AccountsViewPage />,
      },
      {
        path: atlasRoutes.accountImportFromMem1Dot0({
          params: { accountId: ":accountId" },
        }).path,
        element: <AccountImportFromMem1Dot0Page />,
      },
      /**
       * =============================================
       *            Space Account Routes
       * =============================================
       */
      {
        path: atlasRoutes.spaceAccounts({}).path,
        element: <SpaceAccountsRootPage />,
      },
      {
        path: atlasRoutes.spaceAccountsView({
          params: { spaceAccountId: ":spaceAccountId" },
        }).path,
        element: <SpaceAccountsViewPage />,
      },
      {
        path: atlasRoutes.spaceAccountSyncClientGroups({
          params: { spaceAccountId: ":spaceAccountId" },
        }).path,
        element: <SpaceAccountSyncClientGroupsPage />,
      },
      {
        path: atlasRoutes.spaceAccountDataImports({
          params: { spaceAccountId: ":spaceAccountId" },
        }).path,
        element: <SpaceAccountDataImportsPage />,
      },
      {
        path: atlasRoutes.spaceAccountSpaceAccountAppCallouts({
          params: { spaceAccountId: ":spaceAccountId" },
        }).path,
        element: <SpaceAccountSpaceAccountAppCalloutsPage />,
      },

      /**
       * =============================================
       *            Legacy Firestore User Routes
       * =============================================
       */
      {
        path: atlasRoutes.legacyFirestoreUsers({}).path,
        element: <LegacyFirestoreUsersRootPage />,
      },
      {
        path: atlasRoutes.legacyFirestoreUsersView({
          params: { legacyFirestoreUserId: ":legacyFirestoreUserId" },
        }).path,
        element: <LegacyFirestoreUsersViewPage />,
      },

      /**
       * =============================================
       *               Session Routes
       * =============================================
       */
      {
        path: atlasRoutes.sessions({}).path,
        element: <SessionsRootPage />,
      },
      /**
       * =============================================
       *               Billing Routes
       * =============================================
       */
      {
        path: atlasRoutes.billing({}).path,
        element: <BillingRootPage />,
      },
      {
        path: atlasRoutes.billingView({ params: { spaceAccountId: ":spaceAccountId" } }).path,
        element: <BillingViewPage />,
      },

      /**
       * =============================================
       *          API Request Records Routes
       * =============================================
       */
      {
        path: atlasRoutes.apiRequestRecords({}).path,
        element: <ApiRequestRecordsRootPage />,
      },
      {
        path: atlasRoutes.apiRequestRecordsView({
          params: { apiRequestRecordId: ":apiRequestRecordId" },
        }).path,
        element: <ApiRequestRecordsViewPage />,
      },
      /**
       * =============================================
       *              Captured Audio Routes
       * =============================================
       */
      {
        path: atlasRoutes.capturedAudio({}).path,
        element: <CapturedAudioRootPage />,
      },
      {
        path: atlasRoutes.capturedAudioView({
          params: { capturedAudioId: ":capturedAudioId" },
        }).path,
        element: <CapturedAudioViewPage />,
      },
      /**
       * =============================================
       *              Captured Email Routes
       * =============================================
       */
      {
        path: atlasRoutes.capturedEmails({}).path,
        element: <CapturedEmailsRootPage />,
      },
      {
        path: atlasRoutes.capturedEmailsView({
          params: { capturedEmailId: ":capturedEmailId" },
        }).path,
        element: <CapturedEmailsViewPage />,
      },
      {
        path: atlasRoutes.sessionsCreate({}).path,
        element: <SessionsCreatePage />,
      },
      {
        path: atlasRoutes.sessionsView({
          params: { sessionId: ":sessionId" },
        }).path,
        element: <SessionsViewPage />,
      },
      {
        path: atlasRoutes.sessionEvents({
          params: { sessionId: ":sessionId" },
        }).path,
        element: <SessionEventsPageWrapper />,
      },
      {
        path: atlasRoutes.sessionArtifactsView({
          params: { artifactId: ":artifactId" },
        }).path,
        element: <SessionArtifactViewPage />,
      },
      {
        path: atlasRoutes.sessionOperationsView({
          params: { operationId: ":operationId" },
        }).path,
        element: <SessionOperationViewPage />,
      },

      /**
       * =============================================
       *               Electric Routes
       * =============================================
       */
      {
        path: atlasRoutes.electricSessions({}).path,
        element: <ElectricSessionsPage />,
      },
      {
        path: atlasRoutes.electricSessionEvents({}).path,
        element: <ElectricSessionEventsPage />,
      },

      /**
       * =============================================
       *               Notes Routes
       * =============================================
       */
      {
        path: atlasRoutes.notes({}).path,
        element: <NotesRootPage />,
      },
      {
        path: atlasRoutes.notesCreate({}).path,
        element: <NotesCreatePage />,
      },
      {
        path: atlasRoutes.notesView({
          params: { noteId: ":noteId" },
        }).path,
        element: <NotesViewPage />,
      },
      {
        path: atlasRoutes.notesSnapshots({
          params: { noteId: ":noteId" },
        }).path,
        element: <NotesSnapshotsPage />,
      },
      {
        path: atlasRoutes.notesUploadAudio({
          params: { noteId: ":noteId" },
        }).path,
        element: <NotesUploadAudioPage />,
      },
      /**
       * =============================================
       *               Collection Routes
       * =============================================
       */
      {
        path: atlasRoutes.collections({}).path,
        element: <CollectionsRootPage />,
      },
      {
        path: atlasRoutes.collectionsView({
          params: { collectionId: ":collectionId" },
        }).path,
        element: <CollectionsViewPage />,
      },

      /**
       * =============================================
       *           Sync Client Group Routes
       * =============================================
       */
      {
        path: atlasRoutes.syncClientGroups({}).path,
        element: <SyncClientGroupsRootPage />,
      },
      {
        path: atlasRoutes.syncClientGroupsView({
          params: { syncClientGroupId: ":syncClientGroupId" },
        }).path,
        element: <SyncClientGroupsViewPage />,
      },

      /**
       * =============================================
       *           Sync Cvr Routes
       * =============================================
       */
      {
        path: atlasRoutes.syncCvrs({}).path,
        element: <SyncCvrsRootPage />,
      },
      {
        path: atlasRoutes.syncCvrsView({
          params: { syncCvrId: ":syncCvrId" },
        }).path,
        element: <SyncCvrsViewPage />,
      },

      /**
       * =============================================
       *           Sync Model Cvr Snapshot Routes
       * =============================================
       */
      {
        path: atlasRoutes.syncModelCvrSnapshots({}).path,
        element: <SyncModelCvrSnapshotsRootPage />,
      },
      {
        path: atlasRoutes.syncModelCvrSnapshotsView({
          params: { syncModelCvrSnapshotId: ":syncModelCvrSnapshotId" },
        }).path,
        element: <SyncModelCvrSnapshotsViewPage />,
      },

      /**
       * =============================================
       *           Sync Model Event Routes
       * =============================================
       */
      {
        path: atlasRoutes.syncModelEvents({}).path,
        element: <SyncModelEventsRootPage />,
      },
      {
        path: atlasRoutes.syncModelEventsView({
          params: { syncModelEventId: ":syncModelEventId" },
        }).path,
        element: <SyncModelEventsViewPage />,
      },

      /**
       * =============================================
       *           Sync Scope Routes
       * =============================================
       */
      {
        path: atlasRoutes.syncScopes({}).path,
        element: <SyncScopesRootPage />,
      },
      {
        path: atlasRoutes.syncScopesView({
          params: { syncScopeId: ":syncScopeId" },
        }).path,
        element: <SyncScopesViewPage />,
      },

      /**
       * =============================================
       *           Sync Operation Routes
       * =============================================
       */
      {
        path: atlasRoutes.syncOperations({}).path,
        element: <SyncOperationsRootPage />,
      },
      {
        path: atlasRoutes.syncOperationsView({
          params: { syncOperationId: ":syncOperationId" },
        }).path,
        element: <SyncOperationsViewPage />,
      },

      /**
       * =============================================
       *               Data Export Routes
       * =============================================
       */
      {
        path: atlasRoutes.dataExports({}).path,
        element: <DataExportsRootPage />,
      },
      {
        path: atlasRoutes.dataExportsCreate({}).path,
        element: <DataExportsCreatePage />,
      },
      {
        path: atlasRoutes.dataExportsView({
          params: { dataExportId: ":dataExportId" },
        }).path,
        element: <DataExportsViewPage />,
      },

      /**
       * =============================================
       *               Data Import Routes
       * =============================================
       */
      {
        path: atlasRoutes.dataImports({}).path,
        element: <DataImportsRootPage />,
      },
      {
        path: atlasRoutes.dataImportsView({
          params: { dataImportId: ":dataImportId" },
        }).path,
        element: <DataImportsViewPage />,
      },
      {
        path: atlasRoutes.dataImportDataImportItems({
          params: { dataImportId: ":dataImportId" },
        }).path,
        element: <DataImportDataImportItemsPage />,
      },

      /**
       * =============================================
       *               Data Import Item Routes
       * =============================================
       */
      {
        path: atlasRoutes.dataImportItems({}).path,
        element: <DataImportItemsRootPage />,
      },
      {
        path: atlasRoutes.dataImportItemsView({
          params: { dataImportItemId: ":dataImportItemId" },
        }).path,
        element: <DataImportItemsViewPage />,
      },

      /**
       * =============================================
       *               Account Transition Info Routes
       * =============================================
       */
      {
        path: atlasRoutes.accountTransitionInfo({}).path,
        element: <AccountTransitionInfoRootPage />,
      },
      {
        path: atlasRoutes.accountTransitionInfoView({
          params: { accountTransitionInfoId: ":accountTransitionInfoId" },
        }).path,
        element: <AccountTransitionInfoViewPage />,
      },

      /**
       * =============================================
       *               App Callouts
       * =============================================
       */
      {
        path: atlasRoutes.appCallouts({}).path,
        element: <AppCalloutsRootPage />,
      },
      {
        path: atlasRoutes.appCalloutsView({
          params: { appCalloutId: ":appCalloutId" },
        }).path,
        element: <AppCalloutsViewPage />,
      },

      /**
       * =============================================
       *        Space Account App Callout Routes
       * =============================================
       */
      {
        path: atlasRoutes.spaceAccountAppCallouts({}).path,
        element: <SpaceAccountAppCalloutsRootPage />,
      },
      {
        path: atlasRoutes.spaceAccountAppCalloutsView({
          params: { spaceAccountAppCalloutId: ":spaceAccountAppCalloutId" },
        }).path,
        element: <SpaceAccountAppCalloutsViewPage />,
      },

      /**
       * =============================================
       *            Batch Account Upgrade Routes
       * =============================================
       */
      {
        path: atlasRoutes.batchAccountUpgrades({}).path,
        element: <BatchAccountUpgradeRootPage />,
      },
      {
        path: atlasRoutes.batchAccountUpgradeCreate({}).path,
        element: <BatchAccountUpgradeCreatePage />,
      },
      {
        path: atlasRoutes.batchAccountUpgradesView({
          params: { batchAccountUpgradeId: ":batchAccountUpgradeId" },
        }).path,
        element: <BatchAccountUpgradeViewPage />,
      },

      /**
       * =============================================
       *               Import Session Routes
       * =============================================
       */
      {
        path: atlasRoutes.imports({}).path,
        element: <ImportsRootPage />,
      },
      {
        path: atlasRoutes.importsDashboard({}).path,
        element: <ImportsDashboardPage />,
      },
      {
        path: atlasRoutes.importsView({
          params: { importSessionId: ":importSessionId" },
        }).path,
        element: <ImportsViewPage />,
      },

      /**
       * =============================================
       *               Utilities Routes
       * =============================================
       */
      {
        path: atlasRoutes.audioRecordings({}).path,
        element: <AudioRecordingsRootPage />,
      },
      {
        path: atlasRoutes.audioRecordingsQualityMetrics({}).path,
        element: <AudioRecordingsQualityMetricsPage />,
      },
      {
        path: atlasRoutes.audioRecordingsCreate({}).path,
        element: <AudioRecordingsCreatePage />,
      },
      {
        path: atlasRoutes.audioRecordingsView({
          params: { audioRecordingId: ":audioRecordingId" },
        }).path,
        element: <AudioRecordingsViewPage />,
      },

      /**
       * =============================================
       *               Note Suggestion Routes
       * =============================================
       */
      {
        path: atlasRoutes.noteSuggestions({}).path,
        element: <NoteSuggestionsRootPage />,
      },
      {
        path: atlasRoutes.noteSuggestionsView({
          params: { noteSuggestionId: ":noteSuggestionId" },
        }).path,
        element: <NoteSuggestionsViewPage />,
      },
      {
        path: atlasRoutes.uploadedFiles({}).path,
        element: <UploadedFilesRootPage />,
      },
      {
        path: atlasRoutes.uploadedFilesCreate({}).path,
        element: <UploadedFilesCreatePage />,
      },
      {
        path: atlasRoutes.uploadedFilesView({
          params: { uploadedFileId: ":uploadedFileId" },
        }).path,
        element: <UploadedFilesViewPage />,
      },
      {
        path: atlasRoutes.calendars({}).path,
        element: <CalendarsRootPage />,
      },
      {
        path: atlasRoutes.capturedWebsites({}).path,
        element: <CapturedWebsitesRootPage />,
      },
      {
        path: atlasRoutes.capturedWebsitesCreate({}).path,
        element: <CapturedWebsitesCreatePage />,
      },
      {
        path: atlasRoutes.capturedWebsitesView({
          params: { capturedWebsiteId: ":capturedWebsiteId" },
        }).path,
        element: <CapturedWebsitesViewPage />,
      },
      {
        path: atlasRoutes.webClips({}).path,
        element: <WebClipsRootPage />,
      },
      {
        path: atlasRoutes.smartViews({}).path,
        element: <SmartViewsRootPage />,
      },
      {
        path: atlasRoutes.smartViewsGetOrGenerate({}).path,
        element: <SmartViewsGetOrGeneratePage />,
      },
      {
        path: atlasRoutes.smartViewsView({ params: { smartViewId: ":smartViewId" } }).path,
        element: <SmartViewsViewPage />,
      },
      {
        path: atlasRoutes.smartEngineRunsView({
          params: { smartEngineRunId: ":smartEngineRunId" },
        }).path,
        element: <SmartEngineRunPage />,
      },
      {
        path: atlasRoutes.webClipsCreate({}).path,
        element: <WebClipsCreatePage />,
      },
      {
        path: atlasRoutes.webClipsView({ params: { webClipId: ":webClipId" } }).path,
        element: <WebClipsViewPage />,
      },
      {
        path: atlasRoutes.sharedItemInvites({}).path,
        element: <SharedItemInvitesRootPage />,
      },
      {
        path: atlasRoutes.sharedItemInvitesCreate({}).path,
        element: <SharedItemInvitesCreatePage />,
      },
      {
        path: atlasRoutes.sharedItemInvitesView({ params: { inviteId: ":inviteId" } }).path,
        element: <SharedItemInvitesViewPage />,
      },
      {
        path: atlasRoutes.appEvents({}).path,
        element: <AppEventsRootPage />,
      },
      {
        path: atlasRoutes.appEventsView({ params: { appEventId: ":appEventId" } }).path,
        element: <AppEventsViewPage />,
      },
      {
        path: atlasRoutes.accountMigrateEmailAddress({
          params: { accountId: ":accountId" },
        }).path,
        element: <AccountMigrateEmailAddressPage />,
      },

      /**
       * =============================================
       *               Admin Routes
       * =============================================
       */
      {
        path: atlasRoutes.adminBadges({}).path,
        element: <AdminBadgesRootPage />,
      },
      {
        path: atlasRoutes.adminBadgesCreate({}).path,
        element: <AdminBadgesCreatePage />,
      },
      {
        path: atlasRoutes.adminBadgesView({
          params: { badgeId: ":badgeId" },
        }).path,
        element: <AdminBadgesViewPage />,
      },
      {
        path: atlasRoutes.adminMemberBadges({}).path,
        element: <AdminMemberBadgesRootPage />,
      },
      {
        path: atlasRoutes.adminMemberBadgesCreate({}).path,
        element: <AdminMemberBadgesCreatePage />,
      },
      {
        path: atlasRoutes.adminMemberBadgesView({
          params: { memberBadgeId: ":memberBadgeId" },
        }).path,
        element: <AdminMemberBadgesViewPage />,
      },
      {
        path: atlasRoutes.adminLinearDashboard({}).path,
        element: <AdminLinearDashboardRootPage />,
      },
      {
        path: atlasRoutes.monetization({}).path,
        element: <MonetizationPage />,
      },

      /**
       * =============================================
       *          Membot (Convex Assistant) Routes
       * =============================================
       */
      {
        path: atlasRoutes.membot({}).path,
        element: <MembotSessionsListPage />,
      },
      {
        path: atlasRoutes.membotSignals({}).path,
        element: <MembotSignalsPage />,
      },
      {
        path: atlasRoutes.membotView({
          params: { sessionId: ":sessionId" },
        }).path,
        element: <MembotSessionDetailPage />,
      },
      {
        path: atlasRoutes.membotViewTab({
          params: { sessionId: ":sessionId", tab: ":tab" },
        }).path,
        element: <MembotSessionDetailPage />,
      },
      {
        path: atlasRoutes.membotThreadsView({
          params: { threadId: ":threadId" },
        }).path,
        element: <MembotThreadDetailPage />,
      },
      {
        path: atlasRoutes.membotRunsView({
          params: { runId: ":runId" },
        }).path,
        element: <MembotRunDetailPage />,
      },

      /**
       * =============================================
       *               Redirection
       * =============================================
       */
      {
        path: atlasRoutes.root({}).path,
        element: <Navigate to={atlasRoutes.home({}).path} />,
      },

      /**
       * =============================================
       *               Fallback
       * =============================================
       */
      {
        path: atlasRoutes.root({ starPattern: true }).path,
        element: <NotFoundPage />,
      },
    ],
  },
  {
    path: atlasRoutes.root({ starPattern: true }).path,
    element: <NotFoundPage />,
  },
];

const ALL_ATLAS_ROUTES: RouteObject[] = [
  {
    path: atlasRoutes.root({}).path,
    element: <AtlasRootOutlet />,
    children: [
      /**
       * =============================================
       *                Public Routes
       * =============================================
       */
      {
        path: atlasRoutes.root({}).path,
        element: <RootPage />,
      },
      {
        path: atlasRoutes.logIn({}).path,
        element: <LogInPage />,
      },
      {
        path: atlasRoutes.logInEmailPassword({}).path,
        element: <LogInEmailPasswordPage />,
      },
      {
        path: atlasRoutes.logOut({}).path,
        element: <LogOutPage />,
      },
      {
        path: atlasRoutes.googleOAuthRedirect({}).path,
        element: <GoogleOAuthRedirectPage />,
      },
      {
        path: atlasRoutes.root({}).path,
        element: <AtlasStoreProviderOutlet />,
        children: [...AUTHENTICATED_ATLAS_ROUTES],
      },
    ],
  },
];

// Required for some ts-exporting weirdness in the latest react-router-dom.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ATLAS_ROUTER: any = createBrowserRouter(ALL_ATLAS_ROUTES);
